import React, { useState, useEffect } from 'react';
import './CollectionPage.css';
import ProductCard from '../common/ProductCard';
import { useParams, Link } from 'react-router-dom';
import * as ProductService from '../../services/ProductService';
import { DIRECT_PRODUCTS } from '../../data/ProductData';
import { CircularProgress, Box } from '@mui/material';
import { Search } from 'lucide-react';
import { Helmet } from 'react-helmet';

const CollectionPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        // Use direct products instead of API
        let allProducts = DIRECT_PRODUCTS.filter(p => p.inStock);
        setProducts(allProducts);
      } catch (error) {
        console.error('Error fetching products:', error);
        setError('Failed to load products. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Filter products when category or search term changes
  useEffect(() => {
    let filtered = products;
    
    // First filter by search term if it exists
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(product => 
        product.name.toLowerCase().includes(term) ||
        (product.description && product.description.toLowerCase().includes(term))
      );
    }
    
    // Then filter by category if not 'all'
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(
        product => product.category.toLowerCase() === selectedCategory.toLowerCase()
      );
    }
    
    setFilteredProducts(filtered);
  }, [selectedCategory, products, searchTerm]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Create structured data for the collection
  const collectionStructuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": filteredProducts.map((product, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Product",
        "name": product.name,
        "image": product.image.startsWith('http') 
          ? product.image 
          : `https://nmdiamond.in${product.image}`,
        "description": product.description || `${product.name} from NM Diamond`,
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "INR",
          "availability": product.inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
          "offerCount": 1
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.5",
          "reviewCount": "10"
        }
      }
    }))
  };

  return (
    <section className="collection-page">
      <Helmet>
        <title>Diamond Collections | NM Diamond</title>
        <meta name="description" content="Explore our exquisite collection of diamond jewelry including rings, necklaces, earrings, and bracelets." />
        <script type="application/ld+json">
          {JSON.stringify(collectionStructuredData)}
        </script>
      </Helmet>
      
      <div className="container">
        <div className="collection-header">
          <h2 className="section-title">Our Collections</h2>
          <p className="section-subtitle">Explore our exquisite jewelry collections</p>
        </div>

        <div className="search-container">
          <div className="search-input-wrapper">
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
            />
            <Search size={20} className="search-icon" />
          </div>
        </div>

        <div className="category-filters">
          <button
            className={selectedCategory === 'all' ? 'active' : ''}
            onClick={() => setSelectedCategory('all')}
          >
            All
          </button>
          <button
            className={selectedCategory === 'rings' ? 'active' : ''}
            onClick={() => setSelectedCategory('rings')}
          >
            Rings
          </button>
          <button
            className={selectedCategory === 'necklaces' ? 'active' : ''}
            onClick={() => setSelectedCategory('necklaces')}
          >
            Necklaces
          </button>
          <button
            className={selectedCategory === 'earrings' ? 'active' : ''}
            onClick={() => setSelectedCategory('earrings')}
          >
            Earrings
          </button>
          <button
            className={selectedCategory === 'bracelets' ? 'active' : ''}
            onClick={() => setSelectedCategory('bracelets')}
          >
            Bracelets
          </button>
        </div>

        <div className="product-grid">
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : filteredProducts.length === 0 ? (
            <div className="no-products-message">
              {searchTerm ? 
                `No products found matching "${searchTerm}" in this category. Try a different search term or category.` :
                'No products found in this category. Check back soon!'
              }
            </div>
          ) : (
            filteredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))
          )}
        </div>
      </div>
    </section>
  );
};

export default CollectionPage;